import React, { useEffect, useRef, useState, useCallback } from 'react';
import { VideoOff, AlertCircle, User } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { useAuth } from '../../contexts/AuthContext';

interface CameraPreviewProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  isCameraEnabled: boolean;
  profilePicture?: string;
  children?: React.ReactNode;
  stream?: MediaStream | null; // Add explicit stream prop to ensure better synchronization
  errorState?: boolean; // Add optional error state prop
  userName?: string; // Optional user name for public interviews (overrides auth context)
}

const CameraPreview: React.FC<CameraPreviewProps> = ({
  videoRef,
  isCameraEnabled,
  profilePicture,
  children,
  stream,
  errorState = false, // Default to false if not provided
  userName // Optional user name for public interviews
}) => {
  // Simplified state management
  const [isStreamReady, setIsStreamReady] = useState(false);
  const playAttemptRef = useRef<number>(0);
  const maxPlayAttempts = 3;
  const { user } = useAuth();
  const [imgError, setImgError] = useState(false);

  // Use userName prop if provided, otherwise fall back to auth context user
  const displayName = userName || user?.name || 'User';
  // Simple function to play the video once we have a stream
  const playVideo = useCallback(() => {
    if (!videoRef.current || !videoRef.current.srcObject || playAttemptRef.current >= maxPlayAttempts) return;
    
    playAttemptRef.current++;
    console.log(`[CameraPreview] Attempting to play video (attempt ${playAttemptRef.current}/${maxPlayAttempts})`);
    
    // Ensure muted for autoplay
    videoRef.current.muted = true;
    
    videoRef.current.play()
      .then(() => {
        console.log('[CameraPreview] Video playback started successfully');
        setIsStreamReady(true);
      })
      .catch(err => {
        console.warn('[CameraPreview] Error playing video:', err);
        // Try once more after a short delay
        if (playAttemptRef.current < maxPlayAttempts) {
          setTimeout(playVideo, 500);
        }
      });
  }, [videoRef]);

  // When stream changes and video element exists, directly attach it
  useEffect(() => {
    // Reset play attempts when stream changes
    playAttemptRef.current = 0;
    
    if (!videoRef.current || !stream) {
      setIsStreamReady(false);
      return;
    }
    
    // Check if we need to update the srcObject
    const currentStream = videoRef.current.srcObject as MediaStream | null;
    if (currentStream !== stream) {
      console.log('[CameraPreview] Attaching new stream to video element');
      videoRef.current.srcObject = stream;
      playVideo();
    } else if (videoRef.current.paused && isCameraEnabled) {
      // If stream is already attached but video is paused, try to play
      playVideo();
    }
  }, [stream, videoRef, playVideo, isCameraEnabled]);

  // Handle camera enabled/disabled state
  useEffect(() => {
    if (!isCameraEnabled) {
      setIsStreamReady(false);
    } else if (videoRef.current?.srcObject && videoRef.current.paused) {
      // If camera is enabled but video is paused, try to play
      playVideo();
    }
  }, [isCameraEnabled, videoRef, playVideo]);

  // Add event listeners for video element events
  useEffect(() => {
    if (!videoRef.current) return;
    
    const videoElement = videoRef.current;
    
    const handleLoadedMetadata = () => {
      console.log('[CameraPreview] Video loadedmetadata event');
      if (isCameraEnabled && videoElement.paused) {
        playVideo();
      }
    };
    
    const handleCanPlay = () => {
      console.log('[CameraPreview] Video canplay event');
      if (isCameraEnabled && videoElement.paused) {
        playVideo();
      }
    };
    
    const handlePlay = () => {
      console.log('[CameraPreview] Video play event');
      setIsStreamReady(true);
    };
    
    // Add event listeners
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('canplay', handleCanPlay);
    videoElement.addEventListener('play', handlePlay);
    
    return () => {
      // Clean up event listeners
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('canplay', handleCanPlay);
      videoElement.removeEventListener('play', handlePlay);
    };
  }, [videoRef, isCameraEnabled, playVideo]);
  
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div className="relative rounded-xl border border-border-light dark:border-border-dark overflow-hidden bg-surface-light/10 dark:bg-surface-dark/10 backdrop-blur-sm w-full h-full">
        <video 
          ref={videoRef}
          autoPlay 
          playsInline 
          muted 
          className={`w-full h-full object-cover transition-opacity duration-300 non-mirrored ${!isCameraEnabled || !isStreamReady ? 'opacity-0' : 'opacity-100'}`}
        />
        
        {(!isCameraEnabled || !isStreamReady) && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-light/20 dark:bg-surface-dark/20">
            {profilePicture ? (
              <div className={`-mt-6 w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-2 ${imgError ? 'border-border-light dark:border-border-dark' : 'border-primary-light dark:border-primary-dark'}`}>
                {!imgError ? (
                  <img
                    src={profilePicture}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-secondary-light dark:bg-secondary-dark/70">
                    <span className="text-white text-5xl md:text-6xl font-bold font-heading">{displayName.charAt(0).toUpperCase()}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="-mt-6 w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center bg-secondary-light dark:bg-secondary-dark/70 border-2 border-border-light dark:border-border-dark">
                <p className='text-white text-5xl md:text-6xl font-medium font-heading'>{displayName.charAt(0).toUpperCase()}</p>
              </div>
            )}
          </div>
        )}
        
        <div className="absolute bottom-3 md:bottom-4 left-0 right-0 flex justify-center">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CameraPreview; 